import { analytics } from '@/lib/analytics'
import { useProfileStore } from '@/store'
import type { ShippingAddress } from '@/store/profile/ProfileStore'
import { Button, ButtonText } from '@/ui/core/button'
import { Input } from '@/ui/core/input'
import { SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

/** Where the kit ships. Kept on the device until the API has a home for it. */
export const AddressForm = ({ onDone }: { onDone: () => void }) => {
	const { shippingAddress, setShippingAddress } = useProfileStore()
	const [form, setForm] = useState<ShippingAddress>(
		shippingAddress ?? { line1: '', line2: '', city: '', state: '', zip: '' }
	)
	const set = (key: keyof ShippingAddress) => (value: string) =>
		setForm((prev) => ({ ...prev, [key]: value }))
	const isValid =
		!!form.line1.trim() && !!form.city.trim() && !!form.state.trim() && !!form.zip.trim()

	const save = () => {
		analytics.trackEvent('shipping-address-saved')
		setShippingAddress({ ...form, line2: form.line2?.trim() || undefined })
		Toast.show({ type: 'success', text1: 'Address saved' })
		onDone()
	}

	return (
		<View className="pt-2 pb-2">
			<SheetTitle glyph="parcel">Shipping Address</SheetTitle>
			<VStack gap={20}>
				<VStack gap={12}>
					<Input
						placeholder="Street address"
						textContentType="streetAddressLine1"
						autoComplete="address-line1"
						returnKeyType="next"
						value={form.line1}
						onChangeText={set('line1')}
					/>
					<Input
						placeholder="Apt, floor (optional)"
						textContentType="streetAddressLine2"
						autoComplete="address-line2"
						returnKeyType="next"
						value={form.line2 ?? ''}
						onChangeText={set('line2')}
					/>
					<Input
						placeholder="City"
						value={form.city}
						onChangeText={set('city')}
						textContentType="addressCity"
						autoComplete="postal-address-locality"
						returnKeyType="next"
					/>
					<HStack fullWidth gap={12}>
						<Input
							className="flex-1"
							placeholder="State"
							textContentType="addressState"
							autoComplete="postal-address-region"
							returnKeyType="next"
							value={form.state}
							onChangeText={set('state')}
							autoCapitalize="characters"
						/>
						<Input
							className="flex-1"
							placeholder="ZIP"
							textContentType="postalCode"
							autoComplete="postal-code"
							returnKeyType="done"
							value={form.zip}
							onChangeText={set('zip')}
							inputMode="numeric"
						/>
					</HStack>
				</VStack>
				<Button fullWidth size="lg" disabled={!isValid} onPress={save}>
					<ButtonText>Save</ButtonText>
				</Button>
			</VStack>
		</View>
	)
}
